import React, { useState } from "react";
import {
  IconButton, Tooltip, CircularProgress, Box,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
} from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import SendIcon from "@mui/icons-material/Send";
import AttachEmailIcon from "@mui/icons-material/AttachEmail";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";

function ConfirmSendDialog({ open, message, onConfirm, onCancel }) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: "1rem", fontWeight: 700 }}>Confirm Send</DialogTitle>
      <DialogContent>
        <Typography variant="body2">{message}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button onClick={onConfirm} variant="contained">Send</Button>
      </DialogActions>
    </Dialog>
  );
}

// One-click direct send to insurer on file
export function SendInvoiceButton({ invoiceID, insurerEmail, onSuccess, onError }) {
  const [confirming, setConfirming] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setConfirming(false);
    setLoading(true);
    try {
      await phappsRequest(API_PATHS.ZEPHYR_ADMIN_SEND_INVOICE, "POST", { invoiceID });
      onSuccess?.(`Invoice #${invoiceID} sent`);
    } catch (err) {
      onError?.(err?.message || "Send failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Tooltip title="Send invoice to insurer">
        <span>
          <IconButton size="small" onClick={() => setConfirming(true)} disabled={loading} sx={{ p: 0.25 }}>
            {loading
              ? <CircularProgress size={14} />
              : <SendIcon fontSize="small" sx={{ color: "#1976d2" }} />}
          </IconButton>
        </span>
      </Tooltip>
      <ConfirmSendDialog
        open={confirming}
        message={`Sending Invoice #${invoiceID} to ${insurerEmail || "insurer on file"} — are you sure?`}
        onConfirm={handleConfirm}
        onCancel={() => setConfirming(false)}
      />
    </>
  );
}

// Send all outstanding invoices for a claim
export function SendAllOutstandingButton({ claimID, insurerEmail, onSuccess, onError }) {
  const [confirming, setConfirming] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setConfirming(false);
    setLoading(true);
    try {
      const res = await phappsRequest(API_PATHS.ZEPHYR_ADMIN_SEND_ALL_OUTSTANDING, "POST", { claimID });
      const { sent, failed } = res.data ?? {};
      onSuccess?.(`Sent ${sent} invoice${sent !== 1 ? "s" : ""}${failed ? `, ${failed} failed` : ""}`);
    } catch (err) {
      onError?.(err?.message || "Send failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Tooltip title="Send all outstanding invoices to insurer">
        <span>
          <IconButton size="small" onClick={() => setConfirming(true)} disabled={loading} sx={{ p: 0.25 }}>
            {loading
              ? <CircularProgress size={14} />
              : <EmailOutlinedIcon fontSize="small" sx={{ color: "#388e3c" }} />}
          </IconButton>
        </span>
      </Tooltip>
      <ConfirmSendDialog
        open={confirming}
        message={`Sending all outstanding invoices for this claim to ${insurerEmail || "insurer on file"} — are you sure?`}
        onConfirm={handleConfirm}
        onCancel={() => setConfirming(false)}
      />
    </>
  );
}

// Open compose modal
export function ComposeEmailButton({ onClick }) {
  return (
    <Tooltip title="Compose email">
      <IconButton size="small" onClick={onClick} sx={{ p: 0.25 }}>
        <AttachEmailIcon fontSize="small" sx={{ color: "#7b1fa2" }} />
      </IconButton>
    </Tooltip>
  );
}

// All three buttons together
export default function ZephyrEmailButtons({ invoiceID, claimID, insurerEmail = "", onOpenCompose, onSuccess, onError }) {
  return (
    <Box sx={{ display: "inline-flex", gap: 0.25 }}>
      {invoiceID && (
        <SendInvoiceButton invoiceID={invoiceID} insurerEmail={insurerEmail} onSuccess={onSuccess} onError={onError} />
      )}
      {claimID && (
        <SendAllOutstandingButton claimID={claimID} insurerEmail={insurerEmail} onSuccess={onSuccess} onError={onError} />
      )}
      {onOpenCompose && (
        <ComposeEmailButton onClick={onOpenCompose} />
      )}
    </Box>
  );
}
